/**
 * QL-281 — Surface-agnostic Goal List body.
 *
 * Renders the goal cards, the "+ Add goal" button and owns the add / edit
 * form dialog and the delete confirm. The advisor entry (`GoalList`) and the public renderer
 * (`PublicGoalList`) inject their own data + mutations, so this file has no
 * network code of its own. Same GUI skeleton as the Wealth Plan widget.
 */

import { getLocalizedValue, goalLimitForFlow } from "@repo/shared";
import type { GoalTypeOption } from "@repo/shared/types";
import { useState } from "react";
import { toast } from "sonner";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useConfirm } from "@/hooks/use-confirm";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import type { GoalListConfig } from "@/lib/types/flow";
import { InfoNote } from "../../info-note";
import { GoalListFormDialog } from "./goal-list-form-dialog";
import { GoalListRow } from "./goal-list-row";
import { T } from "./goal-list-view.i18n";
import type {
  GoalListCreateData,
  GoalListGoal,
  GoalListMutationResult,
  GoalListUpdateData,
  GoalListUpdateResult,
} from "./types";

type GoalListViewProps = {
  flowInstanceId: string;
  goals: GoalListGoal[];
  goalTypes: GoalTypeOption[];
  config: GoalListConfig;
  language: string;
  /** Locked instance — no add/edit/delete controls. */
  disabled: boolean;
  /** Single-goal flows never offer a second goal (the service rejects it too). */
  multiGoal: boolean;
  /** QL-593 — the flow's goal cap while multiGoal is on (the service enforces it too). */
  maxGoals: number;
  isPending: boolean;
  /** QL-188 — group answering note shown under the header; null for non-group clients. */
  groupNote?: string | null;
  onCreate: (data: GoalListCreateData) => Promise<GoalListMutationResult>;
  onUpdate: (goalId: string, data: GoalListUpdateData) => Promise<GoalListUpdateResult>;
  onDelete: (goalId: string) => Promise<GoalListMutationResult>;
};

export function GoalListView({
  flowInstanceId,
  goals,
  goalTypes,
  config,
  language,
  disabled,
  multiGoal,
  maxGoals,
  isPending,
  groupNote = null,
  onCreate,
  onUpdate,
  onDelete,
}: GoalListViewProps) {
  const t = useT(T);
  const { tenantDefaultLanguage } = useUserLanguage();
  const [addOpen, setAddOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<GoalListGoal | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();

  const showDescription = config.showDescription === true;
  const iconFor = (goal: GoalListGoal): string | null =>
    goalTypes.find((gt) => gt.id === goal.goalType)?.iconSvg ?? null;
  const platformLabelFor = (goal: GoalListGoal): string =>
    goal.platform
      ? getLocalizedValue(goal.platform.labelI18n, language, tenantDefaultLanguage) ||
        Object.values(goal.platform.labelI18n ?? {}).find((v) => v?.trim()) ||
        ""
      : "";

  // The button is hidden, not just guarded, once the flow cannot take another
  // goal — the tab strip's limit and the single-goal rule apply here too.
  const goalLimit = goalLimitForFlow({ multiGoal, maxGoals });
  const canAdd = !disabled && goals.length < goalLimit;

  const handleAdd = () => {
    if (!multiGoal && goals.length >= 1) {
      toast.error(t.singleGoalFlow);
      return;
    }
    if (goals.length >= goalLimit) {
      toast.error(t.maxGoalsReached(goalLimit));
      return;
    }
    setAddOpen(true);
  };

  const handleDelete = async (goal: GoalListGoal) => {
    if (goals.length <= 1) {
      toast.error(t.cannotDeleteLastGoal);
      return;
    }
    const confirmed = await confirm({
      title: t.deleteGoal,
      description: t.deleteConfirmation(goal.name),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });
    if (!confirmed) return;

    const result = await onDelete(goal.id);
    if (!result.success) {
      toast.error(t.error, { description: result.error || t.failedToDeleteGoal });
      return;
    }
    if (editingGoal?.id === goal.id) setEditingGoal(null);
    toast.success(t.goalDeleted);
  };

  return (
    <>
      <VisualizationContainer config={config}>
        {groupNote && <InfoNote text={groupNote} />}
        {goals.length === 0 ? (
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground">{t.noGoals}</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {goals.map((goal) => (
              <GoalListRow
                key={goal.id}
                goal={goal}
                iconSvg={iconFor(goal)}
                platformLabel={platformLabelFor(goal)}
                disabled={disabled}
                canDelete={goals.length > 1}
                isPending={isPending}
                onEdit={() => setEditingGoal(goal)}
                onDelete={() => handleDelete(goal)}
              />
            ))}
          </div>
        )}

        {canAdd && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleAdd}
            disabled={isPending}
            className="w-full"
          >
            {t.addGoal}
          </Button>
        )}
      </VisualizationContainer>

      <GoalListFormDialog
        open={addOpen || editingGoal !== null}
        goal={editingGoal}
        flowInstanceId={flowInstanceId}
        goalTypes={goalTypes}
        showDescription={showDescription}
        onClose={() => {
          setAddOpen(false);
          setEditingGoal(null);
        }}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />

      <ConfirmationDialog />
    </>
  );
}
